import { axios } from '@/api/axios';

import { REQUEST } from '@/constants/api';
import { COUNTRY_PHONE_CODES } from '@/constants/countryCodes';

import {
  ConsentDocument,
  UpdateBiometricConsentResponsePayload,
  UpdateDepositInfoRequestPayload,
  UpdateDepositInfoResponsePayload,
  UpdateDocumentAgreementRequestPayload,
  UpdateDocumentAgreementResponsePayload,
  UpdateEmploymentInfoPayload,
  UpdateEXCPersonalInfoPayload,
  UpdateInfoAccuracyRequest,
  UpdateInfoRequestPayload,
  UpdateInfoResponsePayload,
  UpdateInfoReviewRequestPayload,
  UpdatePhoneNumberRequestPayload,
  UpdatePhoneNumberResponsePayload,
  UpdateRemediationDepositInfoRequest,
} from '@/types/api/user';
import { Language } from '@/types/i18n';
import { UsernameValidationResult } from '@/types/user';

import { getPhoneString, nullIfEmptyString } from '@/utils/string';

export const confirmPersonalDetails = async () => {
  const result = await axios.post(REQUEST.USER.CONFIRM_PERSONAL_DETAILS);
  return result.data;
};

export const updateInfoReview = async ({
  reviewed,
}: UpdateInfoReviewRequestPayload) => {
  const result = await axios.put(REQUEST.USER.INFO_REVIEW, { reviewed });

  return result.data;
};

export const updateInfo = async ({
  ...payload
}: Partial<UpdateInfoRequestPayload>) => {
  const result = await axios.patch<UpdateInfoResponsePayload>(
    REQUEST.USER.INFO,
    payload,
  );

  return result.data;
};

export const updateDepositInfo = async (
  payload: UpdateDepositInfoRequestPayload,
) => {
  const result = await axios.put<UpdateDepositInfoResponsePayload>(
    REQUEST.ACCOUNT.DEPOSIT_INFO,
    payload,
  );

  return result.data;
};

export const updateRemediationDepositInfo = async ({
  payload,
}: UpdateRemediationDepositInfoRequest) => {
  const result = await axios.put<UpdateDepositInfoResponsePayload>(
    REQUEST.REMEDIATION.DEPOSIT_INFO,
    payload,
  );

  return result.data;
};

export type UpdateAccountTypeOrderRequestPayload = {
  primaryAccountId: string;
  secondaryAccountId?: string;
};
export const updateAccountTypeOrder = async (
  payload: UpdateAccountTypeOrderRequestPayload,
) => {
  const result = await axios.post<string>(
    REQUEST.USER.ACCOUNT_TYPE_ORDER_SELECT,
    Object.fromEntries(Object.entries(payload).filter(i => !!i)),
  );
  return result.data;
};

export const updateChargeAccount = async (payload: { accountNo: string }) => {
  const result = await axios.post<string>(REQUEST.USER.CHARGE_ACCOUNT, payload);
  return result.data;
};

export const updateInfoAccuracy = async ({
  infoAccuracyConfirmed,
}: UpdateInfoAccuracyRequest) => {
  const result = await axios.put<UpdateInfoResponsePayload>(
    REQUEST.USER.INFO_ACCURACY,
    { infoAccuracyConfirmed },
  );

  return result.data;
};

export const getConsentDocuments = async () => {
  const result = await axios.get<ConsentDocument[]>(
    REQUEST.USER.CONSENT_DOCUMENTS,
  );

  return result.data;
};

export const updateDocumentAgreement = async (
  payload: UpdateDocumentAgreementRequestPayload,
) => {
  const result = await axios.put<UpdateDocumentAgreementResponsePayload>(
    REQUEST.USER.DOCUMENT_AGREEMENT,
    payload,
  );

  return result.data;
};

type UpdateLanguageRequestPayload = {
  language: Language;
};

export const updateLanguage = async ({
  language,
}: UpdateLanguageRequestPayload) => {
  const result = await axios.post(REQUEST.USER.LANGUAGE, {
    language: language,
  });

  return result.data;
};

export const removeEmploymentDetails = async () => {
  const result = await axios.delete<string>(REQUEST.EMPLOYMENT.ROOT);

  return result.data;
};

export const updateEmploymentInfo = async (
  payload: Partial<UpdateEmploymentInfoPayload>,
) => {
  const { currentUser, employmentDetailed } = payload;

  if (!employmentDetailed) {
    return;
  }

  const callback = currentUser?.employmentDetailed ? axios.put : axios.post;

  const result = await callback(
    REQUEST.EMPLOYMENT.ROOT,
    prepareEmploymentDetailPayload(payload?.employmentDetailed),
  );
  return result.data;
};

function prepareEmploymentDetailPayload(
  employmentDetailed: Partial<UpdateEmploymentInfoPayload>['employmentDetailed'],
) {
  return {
    ...employmentDetailed,
    businessCategoryCode: nullIfEmptyString(
      employmentDetailed?.businessCategoryCode,
    ),
  };
}

export const updatePhoneNumber = async (
  payload: UpdatePhoneNumberRequestPayload,
) => {
  const result = await axios.post<UpdatePhoneNumberResponsePayload>(
    REQUEST.USER.PHONE_NUMBER,
    {
      phoneNumber: getPhoneString(payload.countryCode, payload.phoneNumber),
    },
  );

  return result.data;
};

export const updateBranch = async (payload: string) => {
  const result = await axios.post<string>(REQUEST.USER.BRANCH, {
    branchUID: payload,
  });

  return result.data;
};

export const validateUsername = async (
  payload: string[],
  signal?: AbortSignal,
) => {
  const result = signal
    ? await axios.post<UsernameValidationResult[]>(
        REQUEST.USER.VALIDATE_USERNAME,
        payload,
        { signal },
      )
    : await axios.post<UsernameValidationResult[]>(
        REQUEST.USER.VALIDATE_USERNAME,
        payload,
      );

  return result.data;
};

export const updateTermsAndConditions = async (payload: {
  [key: string]: boolean;
}) => {
  const result = await axios.put<UpdatePhoneNumberResponsePayload>(
    REQUEST.USER.TERMS_AND_CONDITIONS,
    payload,
  );

  return result.data;
};

export const updateBiometricConsent = async (payload: {
  biometricConsent: boolean;
}) => {
  const result = await axios.put<UpdateBiometricConsentResponsePayload>(
    REQUEST.USER.BIOMETRIC_CONSENT,
    payload,
  );

  return result.data;
};

export const getBiometricConsent = async () => {
  const result = await axios.get<UpdateBiometricConsentResponsePayload>(
    REQUEST.USER.BIOMETRIC_CONSENT,
  );

  return result.data;
};

export const updateEXCPersonalInfo = async (
  payload: UpdateEXCPersonalInfoPayload,
) => {
  const APIToCalls: Promise<unknown>[] = [];
  const APIOrders: string[] = []; // To track which API call failed/succeeded then return a correct list of errors
  const { currentUser } = payload.employmentDetailed;
  const { phoneNumber, countryCode, ...infoRequest } =
    payload.infoRequest || {};
  if (
    payload.employmentDetailed.employmentDetailed &&
    Object.keys(payload.employmentDetailed.employmentDetailed).length > 0
  ) {
    APIToCalls.push(updateEmploymentInfo(payload.employmentDetailed));
    APIOrders.push('employmentDetailed');
  }
  if (infoRequest && Object.keys(infoRequest).length > 0) {
    APIToCalls.push(updateInfo({ ...infoRequest }));
    APIOrders.push('infoRequest');
  }
  if (phoneNumber || countryCode) {
    APIToCalls.push(
      updatePhoneNumber({
        phoneNumber,
        countryCode: countryCode || COUNTRY_PHONE_CODES[currentUser.tenant],
      }),
    );
    APIOrders.push('phoneNumber');
  }

  if (APIToCalls.length === 0) {
    return;
  }

  const results = await Promise.allSettled(APIToCalls);
  const isAllFulfilled = results.every(result => result.status === 'fulfilled');
  const errors = results.reduce(
    (acc, result, index) => {
      if (result.status === 'rejected') {
        acc[APIOrders[index]] = {
          error: result.reason.response.data,
        };
      }
      return acc;
    },
    {} as Record<string, { error: string }>,
  );

  if (isAllFulfilled) {
    await updateInfoReview({
      reviewed: true,
    });
  } else {
    throw errors;
  }

  return 'fulfilled';
};

type UpdateUserDocumentInfoPayload = {
  documentId: number | string;
  payload: {
    issueCity: string | null;
    issueCountry: string | null;
    issueDate: string | null;
    idExpiryDate: string;
    idDocNumber: string;
    idDocType: string;
  };
};

type UpdateUserDocumentInfoRequest = {
  docType: string;
  docNumber: string;
  issueDate: string | null;
  expiryDate: string;
  issueCountry: string | null;
  issueCity: string | null;
};

export const updateUserDocumentInfo = async (
  request: UpdateUserDocumentInfoPayload,
) => {
  const { documentId, payload } = request;
  const requestBody: UpdateUserDocumentInfoRequest = {
    docType: payload.idDocType,
    docNumber: payload.idDocNumber,
    issueDate: payload.issueDate,
    expiryDate: payload.idExpiryDate,
    issueCountry: payload.issueCountry,
    issueCity: payload.issueCity,
  };
  const result = await axios.post<{
    message: string;
  }>(`${REQUEST.DOCUMENT.CONFIRM_INFO}/${documentId}`, requestBody);
  return result.data;
};

export type UpdateMinorHolderInfoPayload = {
  operationId: string;
  clientId: number;
  payload: Partial<UpdateInfoRequestPayload>;
};

export const updateMinorHolderInfo = async (
  request: UpdateMinorHolderInfoPayload,
) => {
  const { operationId, clientId, payload } = request;
  const result = await axios.patch<{ message: string }>(
    REQUEST.OPERATION.UPDATE_HOLDER(operationId, `${clientId}`),
    payload,
  );
  return result.data;
};
